import { useState } from 'react'
import { InputBase, Stack, Typography } from '@mui/material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { fieldInputSx } from '@/apps/latest/shared/styles/inlineInput'

import { addDate, isPastDate, localToday, replaceDate } from './carePlanDates'
import { labelSx } from './HomecarePatientPanel'

interface Props {
  dates: string[]
  // 最新の版だけ直せる。前の版は読むだけ
  editable: boolean
  onChange: (dates: string[]) => void
}

const dateSx = {
  ...fieldInputSx,
  flex: 'none',
  width: 148,
  fontVariantNumeric: 'tabular-nums',
} as const

// 札 1 つ。打っている途中の日付 (年の桁を打ち切る前など) を保存しないよう、離れた時点で確定する。
// value が空の札は足す場所で、確定すると空に戻る。
const DateChip = ({
  value,
  disabled,
  min,
  label,
  onCommit,
}: {
  value: string
  disabled: boolean
  min: string
  label: string
  onCommit: (next: string) => void
}) => {
  const [draft, setDraft] = useState(value)
  return (
    <InputBase
      type="date"
      value={draft}
      disabled={disabled}
      inputProps={{
        'aria-label': label,
        min,
      }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== value) onCommit(draft)
        if (!value) setDraft('')
      }}
      sx={{
        ...dateSx,
        // 過ぎた札は打てる欄に見せない (白い地と枠を外す)
        '&.Mui-disabled': {
          bgcolor: 'transparent',
          boxShadow: 'none',
          color: colorTokens.fg.faint,
        },
        '& .Mui-disabled': { WebkitTextFillColor: colorTokens.fg.faint },
      }}
    />
  )
}

// 計画書の訪問予定日の札。札を押すと日付を選べ、最後の空の札が足す場所。日付を消すと札が消える。
// 今日より前の札は押せない (済んだ訪問の日を後から予定に入れない。design-book ADR-0058)。
// 選ぶのは端末の日付の入力 (iPad では暦のホイール) に任せる。
export const ScheduledDateChips = ({ dates, editable, onChange }: Props) => {
  const today = localToday()
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1}>
      <Typography component="span" sx={{ ...labelSx, pt: 0.75 }}>
        訪問予定日
      </Typography>
      <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.5}>
        {dates.map((value, index) => (
          <DateChip
            key={value}
            value={value}
            disabled={!editable || isPastDate(value, today)}
            min={today}
            label={`訪問予定日 ${value}`}
            onCommit={(next) => onChange(replaceDate(dates, index, next))}
          />
        ))}
        {editable && (
          <DateChip
            key={`new-${dates.join()}`}
            value=""
            disabled={false}
            min={today}
            label="訪問予定日を足す"
            onCommit={(next) => onChange(addDate(dates, next))}
          />
        )}
        {!editable && dates.length === 0 && (
          <Typography
            component="span"
            sx={{
              pt: 0.75,
              fontSize: labelSx.fontSize,
              color: colorTokens.fg.muted,
            }}
          >
            (なし)
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}
